'use client'

import { useState } from 'react'
import { formatDateString } from '@/lib/date'
import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover'
import { Calendar } from '@/ui/calendar'
import {
  PlusIcon,
  HeartIcon,
  UsersIcon,
  BriefcaseIcon,
  CalendarBlankIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useCreatePerson } from '@/entries'
import { cn } from '@/lib/tailwind'
import type { Database } from '@/account'

type PersonCategory = Database['public']['Enums']['person_category']

const CATEGORY_OPTIONS: {
  value: PersonCategory
  label: string
  Icon: typeof HeartIcon
}[] = [
  { value: 'family', label: 'Family', Icon: HeartIcon },
  { value: 'friend', label: 'Friend', Icon: UsersIcon },
  { value: 'work', label: 'Work', Icon: BriefcaseIcon },
]

interface AddPersonDialogProps {
  /** Trigger element - if not provided, uses default button */
  trigger?: React.ReactNode
  /** Called after successfully adding a person */
  onSuccess?: () => void
  /** Controlled open state */
  open?: boolean
  /** Called when open state changes */
  onOpenChange?: (open: boolean) => void
}

/**
 * A popover dialog for quickly adding a new person.
 * Designed to be triggered from the people multi-select.
 * Can be controlled or uncontrolled.
 */
export function AddPersonDialog({
  trigger,
  onSuccess,
  open: controlledOpen,
  onOpenChange,
}: AddPersonDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen
  const [name, setName] = useState('')
  const [category, setCategory] = useState<PersonCategory>('friend')
  const [birthday, setBirthday] = useState<Date | undefined>(undefined)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const createPerson = useCreatePerson()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setError('Name is required')
      return
    }

    setError(null)

    createPerson.mutate(
      {
        name: name.trim(),
        category,
        birthday: birthday ? formatDateString(birthday) : null,
        notes: null,
      },
      {
        onSuccess: () => {
          setName('')
          setCategory('friend')
          setBirthday(undefined)
          setOpen(false)
          onSuccess?.()
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'Failed to add person')
        },
      },
    )
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      // Reset form when closing
      setName('')
      setCategory('friend')
      setBirthday(undefined)
      setError(null)
    }
  }

  const formatBirthdayDisplay = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  // In controlled mode without a trigger, just render the Dialog
  // In uncontrolled mode, render with the trigger
  const dialogContent = (
    <DialogContent className="sm:max-w-sm">
      <DialogHeader>
        <DialogTitle>Add New Person</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="person-name" className="text-sm font-medium">
            Name
          </label>
          <Input
            id="person-name"
            placeholder="e.g., Sarah Chen"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (error) setError(null)
            }}
            disabled={createPerson.isPending}
            autoFocus
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Category</label>
          <Select
            value={category}
            onValueChange={(v) => setCategory(v as PersonCategory)}
            disabled={createPerson.isPending}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  <span className="flex items-center gap-2">
                    <opt.Icon className="h-4 w-4" />
                    {opt.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            Birthday{' '}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                disabled={createPerson.isPending}
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !birthday && 'text-muted-foreground',
                )}
              >
                <CalendarBlankIcon className="mr-2 h-4 w-4" />
                {birthday ? formatBirthdayDisplay(birthday) : 'Select date'}
                {birthday && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setBirthday(undefined)
                    }}
                    className="text-muted-foreground hover:text-foreground ml-auto rounded-sm p-0.5"
                    aria-label="Clear birthday"
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={birthday}
                onSelect={(date) => {
                  setBirthday(date)
                  setCalendarOpen(false)
                }}
                captionLayout="dropdown"
                fromYear={1920}
                toYear={new Date().getFullYear()}
                defaultMonth={birthday || new Date(2000, 0)}
                disabled={(date) => date > new Date()}
              />
            </PopoverContent>
          </Popover>
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={createPerson.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createPerson.isPending || !name.trim()}
          >
            {createPerson.isPending ? 'Adding...' : 'Add Person'}
          </Button>
        </div>
      </form>
    </DialogContent>
  )

  // If trigger is provided, wrap in DialogTrigger
  if (trigger) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        {dialogContent}
      </Dialog>
    )
  }

  // In controlled mode without trigger, just render the dialog
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog>
  )
}

/**
 * Hook to open the add person dialog programmatically.
 * Returns a function that can be passed to MultiSelectProperty's onAddNew.
 */
export function useAddPersonDialog() {
  const [isOpen, setIsOpen] = useState(false)
  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
  }
}
